import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPersonRunning, faBriefcaseMedical, faChild } from '@fortawesome/free-solid-svg-icons';
import './ProductsCatalog.css';

const CATEGORIES = [
    { id: 'sports', icon: faPersonRunning },
    { id: 'patology', icon: faBriefcaseMedical },
    { id: 'child', icon: faChild },
];

const PRODUCTS = {
    sports: [
        { id: 'run_slim', image: '/products/sports/IMG_8674.webp' },
        { id: 'soccer', image: '/products/sports/IMG_8675.webp' },
        { id: 'outdoor', image: '/products/sports/IMG_8676.webp' },
        { id: 'cycle', image: '/products/sports/IMG_8677.webp' },
        { id: 'golf', image: '/products/sports/IMG_8678.webp' },
        { id: 'team_sport', image: '/products/sports/IMG_8679.webp' },
        { id: 'sport_wide', image: '/products/sports/IMG_8680.webp' },
        { id: 'sport_standard', image: '/products/sports/IMG_8681.webp' },
        { id: 'sport_low', image: '/products/sports/IMG_8682.webp' },
        { id: 'winter', image: '/products/sports/IMG_8683.webp' },
        { id: 'run', image: '/products/sports/IMG_8684.webp' },
    ],
    patology: [
        { id: 'heel_woman', image: '/products/patology/IMG_8667.webp' },
        { id: 'heel_man', image: '/products/patology/IMG_8668.webp' },
        { id: 'meta_man', image: '/products/patology/IMG_8669.webp' },
        { id: 'anti_pronation', image: '/products/patology/IMG_8670.webp' },
        { id: 'anti_supination', image: '/products/patology/IMG_8671.webp' },
        { id: 'stability', image: '/products/patology/IMG_8672.webp' },
    ],
    child: [
        { id: 'sport_jr', image: '/products/child/IMG_8686.webp' },
        { id: 'everyday_jr', image: '/products/child/IMG_8687.webp' },
        { id: 'child_girl', image: '/products/child/IMG_8688.webp' },
    ],
};

const MIN_SWIPE = 50;

const ProductsCatalog = () => {
    const { t } = useTranslation();
    const [activeCategory, setActiveCategory] = useState('sports');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [visibleCount, setVisibleCount] = useState(4);
    const touchRef = useRef({ startX: 0, endX: 0 });
    const trackRef = useRef(null);

    const items = PRODUCTS[activeCategory] || [];
    const maxIndex = Math.max(0, items.length - visibleCount);

    const updateVisibleCount = useCallback(() => {
        const w = window.innerWidth;
        if (w < 480) setVisibleCount(1);
        else if (w < 768) setVisibleCount(2);
        else if (w < 1200) setVisibleCount(3);
        else setVisibleCount(4);
    }, []);

    useEffect(() => {
        updateVisibleCount();
        window.addEventListener('resize', updateVisibleCount);
        return () => window.removeEventListener('resize', updateVisibleCount);
    }, [updateVisibleCount]);

    useEffect(() => {
        setCurrentIndex(0);
    }, [activeCategory]);

    useEffect(() => {
        if (currentIndex > maxIndex) setCurrentIndex(maxIndex);
    }, [visibleCount, maxIndex, currentIndex]);

    const prev = () => setCurrentIndex((i) => Math.max(0, i - 1));
    const next = () => setCurrentIndex((i) => Math.min(maxIndex, i + 1));

    const onTouchStart = (e) => {
        touchRef.current.startX = e.touches[0].clientX;
        touchRef.current.endX = e.touches[0].clientX;
    };
    const onTouchMove = (e) => {
        touchRef.current.endX = e.touches[0].clientX;
    };
    const onTouchEnd = () => {
        const diff = touchRef.current.startX - touchRef.current.endX;
        if (diff > MIN_SWIPE) next();
        else if (diff < -MIN_SWIPE) prev();
    };

    const cardWidthPercent = 100 / visibleCount;
    const translateX = -(currentIndex * cardWidthPercent);

    return (
        <section id="catalog" className="section catalog-section">
            <div className="container">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <h2 className="section-title">{t('catalog.title')}</h2>
                    <p className="section-subtitle">{t('catalog.subtitle')}</p>
                </motion.div>

                <div className="catalog-tabs">
                    {CATEGORIES.map((cat) => (
                        <button
                            key={cat.id}
                            type="button"
                            className={`catalog-tab ${activeCategory === cat.id ? 'active' : ''}`}
                            onClick={() => setActiveCategory(cat.id)}
                        >
                            <FontAwesomeIcon icon={cat.icon} className="catalog-tab-icon" />
                            {t(`catalog.categories.${cat.id}`)}
                        </button>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeCategory}
                        className="catalog-carousel"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                    >
                        {/* Arrows */}
                        {items.length > visibleCount && (
                            <>
                                <button
                                    className="catalog-arrow catalog-arrow--prev"
                                    onClick={prev}
                                    disabled={currentIndex === 0}
                                    aria-label="Previous"
                                    type="button"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <button
                                    className="catalog-arrow catalog-arrow--next"
                                    onClick={next}
                                    disabled={currentIndex >= maxIndex}
                                    aria-label="Next"
                                    type="button"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </>
                        )}

                        {/* Track */}
                        <div
                            className="catalog-track-wrapper"
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                        >
                            <div
                                className="catalog-track"
                                ref={trackRef}
                                style={{
                                    transform: `translateX(${translateX}%)`,
                                    transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                }}
                            >
                                {items.map((product) => (
                                    <div
                                        key={product.id}
                                        className="catalog-slide"
                                        style={{ width: `${cardWidthPercent}%` }}
                                    >
                                        <div className="catalog-card">
                                            <div className="catalog-card-image">
                                                <img
                                                    src={product.image}
                                                    alt={t(`catalog.products.${product.id}.name`)}
                                                    loading="lazy"
                                                    decoding="async"
                                                    width="520"
                                                    height="924"
                                                />
                                            </div>
                                            <div className="catalog-card-body">
                                                <h3 className="catalog-card-name">
                                                    {t(`catalog.products.${product.id}.name`)}
                                                </h3>
                                                <p className="catalog-card-desc">
                                                    {t(`catalog.products.${product.id}.description`)}
                                                </p>
                                                <span className="catalog-card-badge">
                                                    {t(`catalog.products.${product.id}.audience`)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Dots */}
                        {items.length > visibleCount && (
                            <div className="catalog-dots">
                                {Array.from({ length: maxIndex + 1 }, (_, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        className={`catalog-dot ${i === currentIndex ? 'active' : ''}`}
                                        onClick={() => setCurrentIndex(i)}
                                        aria-label={`Page ${i + 1}`}
                                    />
                                ))}
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </section>
    );
};

export default ProductsCatalog;
